import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Text,
  Box,
  FormControl,
  FormLabel,
  Button,
  VStack,
  Alert,
  AlertDescription,
  AlertIcon,
  useToast,
  Input,
} from '@chakra-ui/react';

import { AttachmentIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import FileUploadCard from 'components/FileUploadCard';

import {
  apiUploadSpecialtyFile,
  apiGetSpecialtyFiles,
  apiDeleteSpecialtyFile,
} from 'api';
import type { APIApplicationFile } from 'types/api.types';

interface SpecialtyFileUploadSectionProps {
  title: string;
  fieldLabel: string;
  fileType: string;
  fileTypeId: number;
  badgeText: string;
  acceptedFileTypes?: string;
  infoAlertText?: string;
  sectionId?: string;
  isRequired?: boolean;
  isLocked?: boolean;
}

const SpecialtyFileUploadSection = ({
  title,
  fieldLabel,
  fileType,
  fileTypeId,
  badgeText,
  acceptedFileTypes,
  infoAlertText,
  sectionId,
  isRequired = true,
  isLocked = false,
}: SpecialtyFileUploadSectionProps) => {
  const { uuid } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Query for existing files
  const { data: files = [] } = useQuery({
    queryKey: ['specialty-files', uuid],
    queryFn: () => apiGetSpecialtyFiles(uuid!),
    refetchOnWindowFocus: false,
  }) as { data: APIApplicationFile[] };

  // Filter for specific file type
  const filteredFiles = files.filter((file) => file.file_type === fileTypeId);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file, fileType }: { file: File; fileType: string }) =>
      apiUploadSpecialtyFile(uuid!, file, fileType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialty-files', uuid] });
      toast({
        title: 'Success',
        description: `${badgeText} uploaded successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Upload failed',
        description:
          error?.response?.data?.error ||
          'Failed to upload file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => apiDeleteSpecialtyFile(uuid!, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialty-files', uuid] });
      toast({
        title: 'File deleted',
        description: `${badgeText} removed successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title: 'Delete failed',
        description: 'Failed to delete file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    uploadMutation.mutate({ file, fileType });

    // Clear the input
    event.target.value = '';
  };

  const handleRemoveFile = (fileId: number) => {
    deleteMutation.mutate(fileId);
  };

  return (
    <Card mb="20px" id={sectionId}>
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        {title}
      </Text>

      <VStack spacing={4} align="stretch">
        <FormControl isRequired={isRequired}>
          <FormLabel fontSize="sm" fontWeight="500" ml="0">
            {fieldLabel}
          </FormLabel>

          <VStack spacing={3} align="stretch">
            <Box>
              <Input
                type="file"
                onChange={handleFileSelect}
                display="none"
                id={`${fileType}-upload`}
                disabled={uploadMutation.isPending}
                accept={acceptedFileTypes}
              />
              <label htmlFor={`${fileType}-upload`}>
                <Button
                  as="span"
                  leftIcon={<AttachmentIcon />}
                  colorScheme="blue"
                  variant="outline"
                  size="md"
                  isLoading={uploadMutation.isPending}
                  loadingText="Uploading..."
                  cursor="pointer"
                  width="full"
                  maxW="300px"
                >
                  Choose File
                </Button>
              </label>
            </Box>

            {filteredFiles.map((file) => (
              <FileUploadCard
                key={file.id}
                file={file}
                badgeText={badgeText}
                badgeColorScheme="blue"
                onDelete={handleRemoveFile}
                isDeleting={deleteMutation.isPending}
                isLocked={isLocked}
              />
            ))}
          </VStack>
        </FormControl>

        {infoAlertText && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              <Text
                fontSize="sm"
                dangerouslySetInnerHTML={{ __html: infoAlertText }}
              />
            </AlertDescription>
          </Alert>
        )}
      </VStack>
    </Card>
  );
};

export default SpecialtyFileUploadSection;
